<template>
  <div class="companions-data__form">
    <div class="companions-data__form-header">
      <p class="companions-data__form-header-title">
        {{ $t('landingpage.checkout-page.companions.add-companion') }}
      </p>
      <button type="button" @click="emit('remove-and-close', companionGuestId)">
        <FontAwesomeIcon :icon="['far', 'xmark']" />
      </button>
    </div>

    <template v-if="showCompanionTicketList">
      <SelectTicket
        :key="`companion-ticket-${companionGuestForm.ticket}`"
        :value="companionGuestForm.ticket"
        :tickets="tickets"
        :add-ons="addOns"
        :assign-add-on="assignAddOnToCompanion"
        @ticket-selected="assignTicketToCompanion"
        @add-on-selected="assignAddOnToCompanion"
        @add-on-removed="removeAddOnFromCompanion"
      >
        <div class="companions-data__form-header-intro">
          <p>{{ $t('landingpage.checkout-page.companions.choose-ticket') }}</p>
        </div>
      </SelectTicket>

      <div class="flex gap-5 mt-6">
        <button
          v-if="orderType === 'add-ons'"
          type="button"
          class="companions-data__form-btn-back btn"
          @click="showCompanionTicketList = false"
        >
          {{ $t('common.back') }}
        </button>

        <button
          type="button"
          class="companions-data__form-ticket-btn-next btn"
          :class="[
            tickets.length && !companionGuestForm.ticket && 'btn-disabled',
            orderType === 'add-ons' && 'flex-1! w-auto!',
          ]"
          :disabled="tickets.length && !companionGuestForm.ticket"
          @click="confirmCompanionTicketSelection"
        >
          {{
            orderType === 'add-ons'
              ? $t(
                  'landingpage.checkout-page.companions.save-accompanying-person',
                )
              : $t('landingpage.checkout-page.companions.continue-details')
          }}
        </button>
      </div>
    </template>

    <template v-else>
      <p class="companions-data__form-header-intro">
        {{ $t('landingpage.checkout-page.companions.enter-details') }}
      </p>

      <form class="companions-data__form-fields">
        <div class="form-container">
          <FormItem
            v-for="item in transformedFields"
            :key="item.id"
            :item="item"
            :backend="backend"
            :form-data="companionGuestForm"
          />
        </div>

        <div class="companions-data__form-footer">
          <button
            v-if="orderType !== 'add-ons'"
            type="button"
            class="companions-data__form-btn-back btn"
            @click="showCompanionTicketList = true"
          >
            {{ $t('common.back') }}
          </button>
          <button
            type="button"
            class="companions-data__form-btn-next btn btn-primary"
            :class="orderType === 'add-ons' && 'col-span-2'"
            @click="
              orderType === 'add-ons'
                ? proceedToTicketSelection()
                : updateCompanionGuest(companionGuestForm)
            "
          >
            {{
              orderType === 'add-ons'
                ? $t(
                    'landingpage.checkout-page.companions.continue-to-additional-bookings',
                  )
                : $t('landingpage.checkout-page.companions.save-companion')
            }}
          </button>
        </div>
      </form>
    </template>
  </div>
</template>

<script setup>
import { ref, inject, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import SelectTicket from '@/Components/LandingPage/Checkout/SelectTicket.vue'
import FormItem from '@landingpage/Registration/components/FormItem.vue'
import {
  postJson,
  getJson,
  putJson,
  useFetch,
} from '@/LandingPage/Checkout/useCheckoutFetch'
import { dataGet, dataSet } from '@landingpage/Common/utils'
import { useCheckoutBackend } from '@landingpage/Registration/composables/useBackendAdapter'
import { useFieldPathResolver } from '@landingpage/Registration/composables/useFieldPathResolver'
import { useVisibilityRules } from '@landingpage/Registration/composables/useVisibilityRules'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  fields: { type: [Array, Object], default: () => ({}) },
  companionId: { type: String, default: null },
})

const emit = defineEmits([
  'close',
  'error',
  'success',
  'is-loading',
  'expired-session',
  'remove-and-close',
])

const { getFieldPath } = useFieldPathResolver()
const { t } = useI18n()

const transformedFields = computed(() => {
  if (!props.fields?.items) return []

  const { evaluateVisibilityRules } = useVisibilityRules(companionGuestForm)

  return props.fields.items
    .filter((item) => {
      if (item.hidden) return false
      if (!item.visibility_rules) return true
      return evaluateVisibilityRules(item.visibility_rules)
    })
    .map((item) => ({
      ...item,
      render_type: item.type,
      // Use nested path for proper form data structure
      form_mapping: getFieldPath(item),
      label: item.field?.label || '',
      options: item.field?.options || [],
      core_id: item.field?.core_id || null,
      limits: item.field?.limits || null,
      display_availability: item.display_availability ?? false,
    }))
})

const generateCompanionGuestFields = (fields) => {
  let initialData = {
    contact: {},
    extended_fields: {},
    booking: { extended_fields: {} },
  }

  if (fields) {
    fields.forEach((field) => {
      const path = getFieldPath(field)
      if (path) {
        dataSet(initialData, path, null)
      }
    })
  }

  return initialData
}

const route = window.route
const showCompanionTicketList = ref(true)
const companionGuestId = ref(props.companionId || null)
const formKey =
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)
let creationPromise = null
const companionGuestForm = ref({
  ticket: null,
  add_ons: [],
  add_ons_remove: [],
  id: companionGuestId.value,
  ...generateCompanionGuestFields(props.fields.items),
})
const errors = ref({})
const tickets = ref([])
const addOns = ref([])
const eventId = inject('eventId')
const orderId = inject('orderId')
const orderType = inject('orderType')
const orderDetails = inject('orderDetails')
const activeLocale = inject('activeLocale', null)
const injectedTickets = inject('tickets')
const injectedAddOns = inject('addOns')
const recalculateFieldLimits = inject('recalculateFieldLimits', null)
const registerCompanionForm = inject('registerCompanionForm', null)
const unregisterCompanionForm = inject('unregisterCompanionForm', null)

const ensureGuestId = async () => {
  if (companionGuestId.value) return companionGuestId.value
  if (creationPromise) return creationPromise
  creationPromise = (async () => {
    const { data, error, statusCode } = await postJson(
      route('api.checkout.guests.store', {
        event: eventId,
        order: orderId.value,
      }),
    )
    if (statusCode.value && statusCode.value === 404) {
      emit('expired-session')
      return null
    }
    if (error.value) {
      emit('error', data.value?.message)
      return null
    }
    const newId = data.value?.data?.guest_id
    if (!newId) return null
    companionGuestId.value = newId
    companionGuestForm.value.id = newId

    if (registerCompanionForm && unregisterCompanionForm) {
      unregisterCompanionForm(`create-${formKey}`)
      registerCompanionForm(newId, companionGuestForm)
    }

    return newId
  })()
  const id = await creationPromise
  creationPromise = null
  return id
}

const backend = useCheckoutBackend({
  data: companionGuestForm,
  initialData: {
    ticket: null,
    add_ons: [],
    ...generateCompanionGuestFields(props.fields.items),
  },
  errors: errors,
  updateField: (path, value) => {
    dataSet(companionGuestForm.value, path, value)
  },
})

onMounted(() => {
  if (orderType.value === 'add-ons') {
    showCompanionTicketList.value = false
  }

  if (registerCompanionForm) {
    registerCompanionForm(`create-${formKey}`, companionGuestForm)
  }

  if (companionGuestId.value) {
    fetchCompanionGuestTickets(companionGuestId.value)
    fetchCompanionGuestAddons(companionGuestId.value)
  }
})

watch(
  () => injectedTickets?.value,
  (value) => {
    if (!companionGuestId.value) tickets.value = value ?? []
  },
  { immediate: true },
)

watch(
  () => injectedAddOns?.value,
  (value) => {
    if (companionGuestId.value) return
    if (!value) {
      addOns.value = []
      return
    }
    const cloned = JSON.parse(JSON.stringify(value))
    cloned.forEach((group) => {
      group.addons?.forEach((addon) => {
        addon.items = []
      })
    })
    addOns.value = cloned
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  if (unregisterCompanionForm) {
    unregisterCompanionForm(companionGuestId.value ?? `create-${formKey}`)
  }
})

watch(
  companionGuestForm,
  () => {
    if (recalculateFieldLimits) recalculateFieldLimits()
  },
  { deep: true },
)

const validate = () => {
  backend.clearErrors()

  const { evaluateVisibilityRules } = useVisibilityRules(companionGuestForm)
  const visibleFields = props.fields.items.filter((item) => {
    if (item.hidden) return false
    if (!item.visibility_rules) return true
    return evaluateVisibilityRules(item.visibility_rules)
  })

  // Only validate visible fields
  visibleFields.forEach((item) => {
    if (item.required) {
      const path = getFieldPath(item)
      const value = dataGet(companionGuestForm.value, path)
      if (
        [null, '', [], false].includes(value) ||
        (Array.isArray(value) && value.length === 0)
      ) {
        backend.addError(
          path,
          t('validation.required', { attribute: item.field.label }),
        )
      }
    }
  })

  const currentErrors = backend.getErrors()
  const isFormValid = Object.keys(currentErrors).length === 0

  return isFormValid
}

// Set validation errors from endpoint call
const setApiErrors = (apiErrors) => {
  backend.clearErrors()

  Object.keys(apiErrors).forEach((key) => {
    const messages = apiErrors[key]

    if (Array.isArray(messages)) {
      messages.forEach((message) => {
        backend.addError(key, message)
      })
    }
  })
}

// Fetch companion guest tickets
const fetchCompanionGuestTickets = async (guestId) => {
  if (!guestId) return
  emit('is-loading', true)
  const { data, statusCode } = await getJson(
    route('api.checkout.guest.tickets.index', {
      event: eventId,
      order: orderId.value,
      guest: guestId,
    }),
  )
  if (statusCode.value && statusCode.value === 404) {
    emit('is-loading', false)
    emit('expired-session')
    return
  }
  tickets.value = data.value.data
  companionGuestForm.value.ticket = tickets.value?.find(
    ({ guest_has_ticket }) => guest_has_ticket,
  )?.id
  emit('is-loading', false)
}

// Fetch companion guest add-ons
const fetchCompanionGuestAddons = async (guestId) => {
  if (!guestId) return
  emit('is-loading', true)
  const { data, statusCode } = await getJson(
    route('api.checkout.guest.addons.index', {
      event: eventId,
      order: orderId.value,
      guest: guestId,
    }),
  )
  if (statusCode.value && statusCode.value === 404) {
    emit('is-loading', false)
    emit('expired-session')
    return
  }
  addOns.value = data.value.data
  addOns.value.forEach((item) => {
    item.addons.forEach((addon) => {
      addon.items.map((lineItem) => {
        lineItem.details = orderDetails.value?.line_items.find(
          ({ option_from, option_to }) => {
            return (
              lineItem.starts_at === option_from &&
              lineItem.ends_at === option_to
            )
          },
        )
      })
    })
  })

  emit('is-loading', false)
}

const persistTicket = async (guestId) => {
  const ticket_id = companionGuestForm.value.ticket
  if (!ticket_id) return true
  const { data, error, statusCode } = await putJson(
    route('api.checkout.guest.ticket.update', {
      event: eventId,
      order: orderId.value,
      guest: guestId,
    }),
    { ticket_id },
  )
  if (statusCode.value && statusCode.value === 404) {
    emit('expired-session')
    return false
  }
  if (error.value) {
    emit('error', data.value?.message)
    return false
  }
  return true
}

const updateCompanionGuest = async (guest) => {
  // check validation
  if (!validate()) return false
  emit('is-loading', true)
  const guestId = await ensureGuestId()
  if (!guestId) {
    emit('is-loading', false)
    return
  }
  const { data, error, statusCode } = await useFetch(
    route('api.checkout.guest.update', {
      event: eventId,
      order: orderId.value,
      guest: guestId,
      locale: activeLocale,
    }),
  ).patch(guest)
  if (statusCode.value && statusCode.value === 404) {
    emit('is-loading', false)
    emit('expired-session')
    return
  }
  if (error.value) {
    if (data.value?.errors) setApiErrors(data.value.errors)
    emit('is-loading', false)
    return
  }

  const ticketOk = await persistTicket(guestId)
  if (!ticketOk) {
    emit('is-loading', false)
    return
  }

  const addOnsOk = await persistSelectedAddOns(guestId)
  if (!addOnsOk) {
    emit('is-loading', false)
    return
  }

  emit('is-loading', false)
  emit('success')
}

const proceedToTicketSelection = () => {
  if (!validate()) return false
  showCompanionTicketList.value = true
}
const assignTicketToCompanion = (ticket_id) => {
  companionGuestForm.value.ticket = ticket_id
}

const confirmCompanionTicketSelection = async () => {
  if (orderType.value === 'add-ons') {
    await updateCompanionGuest(companionGuestForm.value)
    return
  }
  showCompanionTicketList.value = false
}
const findCatalogAddOn = (addOnId) => {
  for (const group of addOns.value) {
    const found = group.addons?.find((a) => a.id === addOnId)
    if (found) return found
  }
  return null
}

const assignAddOnToCompanion = async (addOn) => {
  const catalogAddOn = findCatalogAddOn(addOn.id)
  if (
    catalogAddOn?.availability_type === 'fixed' &&
    !catalogAddOn.items?.length
  ) {
    catalogAddOn.items = [
      {
        id: `pending-${addOn.id}-${Date.now()}`,
        quantity: addOn.quantity,
      },
    ]
  }
  return true
}

const removeAddOnFromCompanion = (addOn) => {
  const catalogAddOn = findCatalogAddOn(addOn.id)
  if (!catalogAddOn) return
  if (catalogAddOn.availability_type === 'flexible' && addOn.reservation_id) {
    catalogAddOn.items = (catalogAddOn.items ?? []).filter(
      (item) => item.id !== addOn.reservation_id,
    )
    return
  }
  catalogAddOn.items = []
}

const persistSelectedAddOns = async (guestId) => {
  for (const group of addOns.value) {
    for (const addon of group.addons ?? []) {
      for (const item of addon.items ?? []) {
        const payload = {
          addon_id: addon.id,
          quantity: item.quantity ?? 1,
        }
        if (item.starts_at) payload.start_at = item.starts_at
        if (item.ends_at) payload.end_at = item.ends_at
        const { data, error, statusCode } = await postJson(
          route('api.checkout.guest.addons.store', {
            event: eventId,
            order: orderId.value,
            guest: guestId,
          }),
          payload,
        )
        if (statusCode.value && statusCode.value === 404) {
          emit('expired-session')
          return false
        }
        if (error.value) {
          emit('error', data.value?.message)
          return false
        }
      }
    }
  }
  return true
}
</script>
